import { get } from 'lodash-es';
import moment, { Moment } from 'moment';
import { MomentInput } from 'moment/moment';
import { getParamFromUrl } from '@/infrastructure/history';
import { DEFAULT_TIME_RANGE_ISSUES } from '@/presentation/components/form/components/time-range';
import { MetricType, TimeType } from '@/proto/metric';

moment.locale('zh-cn');
moment.updateLocale('zh-ch', {
  weekdaysMin: ['日', '一', '二', '三', '四', '五', '六'],
});
export const EXPORT_FILE_FORMAT = 'YYYYMMDDHHmmss';
export const DATE_TIME_FORMAT = 'YYYY-MM-DD HH:mm:ss';
export const DATE_MILLISECOND = 'YYYY-MM-DD HH:mm:ss SSS';
export const DATE_FORMAT = 'YYYY-MM-DD';
export const HH_MM_SS = 'HH:mm:ss';
export const formatTimestamp = (time: number | string | undefined | null, format: string, isMillisecond = false) => {
  if (!time) {
    return '';
  }
  let formatTime = typeof(time) === 'string' ? parseInt(time, 10) : time;
  if (!isMillisecond) {
    formatTime = formatTime * 1000;
  }
  return moment(formatTime).format(format);
};

export type TimeRange = [Moment, Moment];

export const setDateHours = (date: Moment, option?: 'start' | 'end'): Moment => {
  if (option === 'start') {
    return date.hour(0)
      .minute(0)
      .second(0);
  }
  if (option === 'end') {
    return date.hour(23)
      .minute(59)
      .second(59);
  }
  return date;
};

const shortCusRanges: Record<string, any> = {
  近1小时: () => CommonDateRange.hour(),
  今天: () => CommonDateRange.today(),
  昨天: () => CommonDateRange.yesterday(),
  近1天: () => CommonDateRange.lastDays(1),
  近2天: () => CommonDateRange.lastDays(2),
  近7天: () => CommonDateRange.lastDays(7),
  近15天: () => CommonDateRange.lastDays(15),
  近30天: () => CommonDateRange.lastDays(30),
  近90天: () => CommonDateRange.lastDays(90),
  近180天: () => CommonDateRange.lastDays(180),
};

export const CommonDateRange: Record<string, (shift?: number, endShift?: number) => TimeRange> = {
  hour: (shift = 1) => [
    moment()
      .subtract(shift, 'hours')
      .second(59),
    moment().second(59),
  ],
  // the date range includes today
  today: () => [
    moment().startOf('day'),
    moment().endOf('day'),
  ],
  yesterday: () => [
    moment()
      .subtract(1, 'days')
      .startOf('day'),
    moment()
      .subtract(1, 'days')
      .endOf('day'),
  ],
  week: (shift = 0) => [
    moment()
      .subtract(6 + shift, 'days')
      .startOf('day'),
    moment()
      .subtract(shift, 'days')
      .endOf('day'),
  ],
  month: (shift = 0) => [
    moment()
      .subtract(29 + shift, 'days')
      .startOf('day'),
    moment()
      .subtract(shift, 'days')
      .endOf('day'),
  ],
  halfSeason: (shift = 0) => [
    moment()
      .subtract(44 + shift, 'days')
      .startOf('day'),
    moment()
      .subtract(shift, 'days')
      .endOf('day'),
  ],
  season: (shift = 0) => [
    moment()
      .subtract(89 + shift, 'days')
      .startOf('day'),
    moment()
      .subtract(shift, 'days')
      .endOf('day'),
  ],
  halfYear: (shift = 0) => [
    moment()
      .subtract(179 + shift, 'days')
      .startOf('day'),
    moment()
      .subtract(shift, 'days')
      .endOf('day'),
  ],
  daysFull: (shift = 1, endShift = 1) => [
    moment()
      .subtract(shift, 'days')
      .startOf('day'),
    moment()
      .subtract(endShift, 'days')
      .endOf('day'),
  ],
  lastDaysFull: (shift = 0) => [
    moment()
      .subtract(shift, 'days')
      .startOf('day'),
    moment().endOf('day'),
  ],
  lastDays: (shift = 0) => [
    moment().subtract(shift, 'days'),
    moment(),
  ],
};

export const getDateRangeByShortCut = (dateStr: string, shortRanges?: Record<string, any>) => {
  const computeFunc = get(shortRanges ?? shortCusRanges, dateStr);
  if (!computeFunc) return null;

  return computeFunc();
};

export const getAndFormatDateRangeByShortCut = (dateStr: string, shortRanges?: Record<string, any>) => {
  const ranges = getDateRangeByShortCut(dateStr, shortRanges);

  if (ranges) {
    return {
      startTime: ranges[0].unix()
        .toString(),
      endTime: ranges[1].unix()
        .toString(),
    };
  }

  return null;
};

export const getDefaultTimeRange = () => {
  const customDate = getParamFromUrl('customDate');
  const ranges = getAndFormatDateRangeByShortCut(customDate);
  return ranges || formatTimeRange(DEFAULT_TIME_RANGE_ISSUES);
};

export const getTodayTimeRange = () => ({
  startTime: moment()
    .startOf('day')
    .unix()
    .toString(),
  endTime: moment()
    .endOf('day')
    .unix()
    .toString(),
});

const formatter = 'YYYY-MM-DDTHH:mm:ssZ';

export function formatStartTime(startTime: string | Moment, format = formatter) {
  return moment(startTime)
    .hours(0)
    .minutes(0)
    .seconds(0)
    .utcOffset(8)
    .format(format);
}

export function formatEndTime(endTime: string | Moment, format = formatter) {
  return moment(endTime)
    .hours(23)
    .minutes(59)
    .seconds(59)
    .utcOffset(8)
    .format(format);
}

export function getTimeWindow(startTime: string, endTime: string, metricType?: MetricType) {
  const start = moment(startTime);
  const end = moment(endTime);
  const diffInDays = end.diff(start, 'days');

  if ((metricType === MetricType.TYPE_ANR || metricType === MetricType.TYPE_FOOM) && diffInDays === 0) {
    const today = moment().startOf('day');
    const yesterday = moment().subtract(1, 'days')
      .startOf('day');
    // 时间范围为某一天且只为当天或者昨天时为5分钟粒度，反之为1小时粒度
    const isFiveMinute = (moment(start).isSame(today, 'day') && moment(end).isSame(today, 'day'))
      || (moment(start).isSame(yesterday, 'day') && moment(end).isSame(yesterday, 'day'));

    return isFiveMinute ? TimeType.MINUTE5 : TimeType.HOUR;
  }

  return diffInDays > 0 ? TimeType.DAY : TimeType.HOUR;
}

export function formatTimeRange(timeRange: string[]) {
  return {
    startTime: moment(timeRange[0])
      .startOf('day')
      .unix()
      .toString(),
    endTime: moment(timeRange[1])
      .endOf('day')
      .unix()
      .toString(),
  };
}

export function formatExactTimeRange(timeRange: string[]) {
  return {
    startTime: moment(timeRange[0])
      .unix()
      .toString(),
    endTime: moment(timeRange[1])
      .unix()
      .toString(),
  };
}

export function transform2TimeRange(startTime: string, endTime: string) {
  return (startTime && endTime)
    ? {
      timeRange: [
        moment((Number(startTime) * 1000)).format('YYYY-MM-DD HH:mm:ss'),
        moment(Number(endTime) * 1000).format('YYYY-MM-DD HH:mm:ss'),
      ],
    }
    : null;
}

export function formatDashboardTimestamp(timestamp: MomentInput, timeType?: TimeType) {
  if (timeType === TimeType.DAY) {
    return moment(timestamp).format('MM-DD');
  }
  if (timeType === TimeType.HOUR) {
    return moment(timestamp).format('HH');
  }
  if (timeType === TimeType.WEEK) {
    return moment(timestamp).format('MM-DD');
  }
  if (timeType === TimeType.MONTH) {
    return moment(timestamp).format('MM-DD');
  }

  return moment(timestamp).format('HH:mm');
}

export function formatLaunchTime(ms: number) {
  if (isNaN(ms) || ms <= 0) {
    return '-';
  }
  if (ms < 1000) {
    return `${ms} 毫秒`;
  }
  if (ms < 60000) {
    // 一分钟内
    const milliseconds = Math.floor(ms % 1000);
    return `${Math.floor(ms / 1000)} 秒 ${milliseconds ? `${milliseconds} 毫秒` : ''}`; // 毫秒为 0 不展示
  }
  if (ms < 60000 * 60) {
    // 一小时内
    const minute = Math.floor(ms / 60000);
    const second = Math.floor((ms - minute * 60000) / 1000);
    const milliseconds = Math.floor((ms - minute * 60000 - second * 1000));
    return `${minute} 分 ${second} 秒 ${milliseconds ? `${milliseconds} 毫秒` : ''}`;
  }
  if (ms < 1000 * 3600 * 24) {
    // 一天内
    const hour = Math.floor(ms / (1000 * 3600));
    const minute = Math.floor((ms - hour * 1000 * 3600) / 60000);
    const second = Math.floor((ms - hour * 1000 * 3600 - minute * 60000) / 1000);
    const milliseconds = Math.floor(ms - hour * 1000 * 3600 - minute * 60000 - second * 1000);
    return `${hour} 小时 ${minute} 分 ${second} 秒 ${milliseconds ? `${milliseconds} 毫秒` : ''}`;
  }
  const day = Math.floor(ms / (1000 * 3600 * 24));
  const hour = Math.floor((ms - day * 1000 * 3600 * 24) / (1000 * 3600));
  const minute = Math.floor((ms - day * 1000 * 3600 * 24 - hour * 1000 * 3600) / 60000);
  const second = Math.floor((ms - day * 1000 * 3600 * 24 - hour * 1000 * 3600 - minute * 60000) / 1000);
  const milliseconds = Math.floor(ms - day * 1000 * 3600 * 24 - hour * 1000 * 3600 - minute * 60000 - second * 1000);
  return `${day} 天 ${hour} 小时 ${minute} 分 ${second} 秒 ${milliseconds ? `${milliseconds} 毫秒` : ''}`;
}

export function formatDate(dateStr: string, format: string, type = 'unix'): string {
  const date = type === 'unix' ? moment.unix(Number(dateStr)) : moment(dateStr);
  if (date.isValid() && dateStr) {
    return date.format(format);
  }
  return dateStr;
}

export function formatMomentTimeRange(timeRange?: Moment[]) {
  return {
    startTime: timeRange?.[0]
      ?.startOf?.('date')
      ?.unix(),
    endTime: timeRange?.[1]
      ?.endOf('date')
      ?.unix(),
  };
}
